import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';

export const Header = ({ onOpenPopup }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Trademark Registration', path: '/services/trademark-registration' },
    { name: 'Copyright Registration', path: '/services/copyright-registration' },
    { name: 'Trademark Comprehensive Search', path: '/services/trademark-search' },
    { name: 'Company Formation', path: '/services/company-formation' },
    { name: 'Amazon Brand Registry', path: '/services/amazon-brand-registry' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-xl shadow-sm' : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <div className="text-2xl font-heading font-bold">
              <img src="/assets/img/brand/logo-dark.png" alt="Trademark Law Consulting" className="h-12" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className="text-slate-700 hover:text-[#E1A334] font-medium transition-colors"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-slate-700 hover:text-[#E1A334] font-medium transition-colors"
              data-testid="nav-about"
            >
              About Us
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="flex items-center text-slate-700 hover:text-[#E1A334] font-medium transition-colors"
                data-testid="nav-services"
              >
                Services
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute left-1/1 transform -translate-x-1/2 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
                    data-testid="services-dropdown"
                  >
                    <div className="p-4 max-h-96 overflow-y-auto">
                      {services.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-4 py-3 text-slate-700 hover:bg-[#E1A334]/10 hover:text-[#E1A334] rounded-lg transition-all duration-200"
                          data-testid={`service-link-${index}`}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link
              to="/our-attorneys"
              className="text-slate-700 hover:text-[#E1A334] font-medium transition-colors"
              data-testid="nav-attorneys"
            >
              Our Attorneys
            </Link>
            <Link
              to="/contact"
              className="text-slate-700 hover:text-[#E1A334] font-medium transition-colors"
              data-testid="nav-contact"
            >
              Contact Us
            </Link>
          </nav>

          {/* Right Side Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={onOpenPopup}
              data-testid="register-trademark-button"
              className="bg-[#E1A334] hover:bg-[#C4912E] text-white font-semibold rounded-full px-6 py-2.5 shadow-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              Register My Trademark
            </Button>
            <Button
              variant="outline"
              data-testid="chat-now-button"
              className="js-chat border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-[#E1A334] rounded-full px-6 py-2.5 font-medium transition-all duration-300"
            >
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-slate-700"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden pb-6"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col space-y-4">
                <Link to="/" className="text-slate-700 hover:text-[#E1A334] font-medium py-2">
                  Home
                </Link>
                <Link to="/about" className="text-slate-700 hover:text-[#E1A334] font-medium py-2">
                  About Us
                </Link>
                <div className="space-y-2">
                  <div className="text-slate-900 font-semibold py-2">Services</div>
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block text-slate-600 hover:text-[#E1A334] pl-4 py-1"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
                <Link to="/our-attorneys" className="text-slate-700 hover:text-[#E1A334] font-medium py-2">
                  Our Attorneys
                </Link>
                <Link to="/contact" className="text-slate-700 hover:text-[#E1A334] font-medium py-2">
                  Contact Us
                </Link>
                <Button
                  onClick={onOpenPopup}
                  className="bg-[#E1A334] text-white font-semibold rounded-full w-full"
                >
                  Register My Trademark
                </Button>
                <Button
                  variant="outline"
                  className="js-chat border-slate-200 text-slate-700 rounded-full w-full"
                >
                  Chat Now
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
